import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, ChevronDown } from "lucide-react";
import { waLink } from "../data/site";
import { SOLUTIONS } from "../data/solutions";
import { PRODUCT_CATEGORIES } from "../data/products";

const linkCls = ({ isActive }) =>
  `font-display text-sm font-semibold uppercase tracking-widest transition-colors duration-200 ${
    isActive ? "text-spark" : "text-gold hover:text-spark"
  }`;

const Drop = ({ label, items, base, testid }) => (
  <div className="group relative">
    <button data-testid={testid} className="flex items-center gap-1 font-display text-sm font-semibold uppercase tracking-widest text-gold transition-colors duration-200 group-hover:text-spark">
      {label}
      <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <div className="invisible absolute left-0 top-full z-50 w-72 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
      <div className="border border-seam bg-iron shadow-2xl">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            data-testid={`nav-drop-${it.to.replace(/\//g, "-")}`}
            className="block border-b border-seam/60 px-5 py-3 font-brand text-sm font-semibold text-gold transition-colors duration-150 last:border-0 hover:bg-steel hover:text-spark"
          >
            {it.label}
          </Link>
        ))}
        <Link to={base} className="block px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-spark hover:bg-steel">
          View all →
        </Link>
      </div>
    </div>
  </div>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const mobileLinks = [
    { to: "/about", label: "About SABAKT" },
    ...SOLUTIONS.map((s) => ({ to: `/solutions/${s.slug}`, label: s.title })),
    ...PRODUCT_CATEGORIES.map((c) => ({ to: `/products#${c.slug}`, label: c.name })),
    { to: "/industries", label: "Industries We Serve" },
    { to: "/projects", label: "Projects" },
    { to: "/insights", label: "Insights" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-seam bg-obsidian/85 backdrop-blur-md" data-testid="main-navbar">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <span className="rounded-sm border border-seam bg-white px-3 py-1.5 shadow-md">
            <img src="/images/logo.png" alt="SABAKT — سبكت للأعمال المعدنية" className="h-9 w-auto" />
          </span>
          <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-cadmium sm:inline">KSA</span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          <NavLink to="/about" className={linkCls} data-testid="nav-about">About</NavLink>
          <Drop testid="nav-solutions-dropdown" label="Solutions" base="/solutions"
            items={SOLUTIONS.map((s) => ({ to: `/solutions/${s.slug}`, label: s.title }))} />
          <Drop testid="nav-products-dropdown" label="Products" base="/products"
            items={PRODUCT_CATEGORIES.map((c) => ({ to: `/products#${c.slug}`, label: c.name }))} />
          <NavLink to="/industries" className={linkCls} data-testid="nav-industries">Industries</NavLink>
          <NavLink to="/projects" className={linkCls} data-testid="nav-projects">Projects</NavLink>
          <NavLink to="/insights" className={linkCls} data-testid="nav-insights">Insights</NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp-btn"
            className="flex items-center gap-2 border border-seam px-4 py-2.5 text-sm font-semibold text-frost transition-colors duration-200 hover:border-spark hover:text-spark">
            <MessageCircle size={16} /> WhatsApp
          </a>
          <button onClick={() => navigate("/rfq")} data-testid="nav-rfq-button"
            className="bg-gold px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-spark">
            Request a Quote
          </button>
        </div>

        <button className="text-frost lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="max-h-[70vh] overflow-y-auto border-t border-seam bg-obsidian px-6 py-4 lg:hidden" data-testid="nav-mobile-menu">
          {mobileLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="block border-b border-seam/50 py-3 font-display text-lg font-semibold uppercase tracking-wider text-gold">
              {l.label}
            </Link>
          ))}
          <div className="flex gap-3 py-5">
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-whatsapp"
              className="flex flex-1 items-center justify-center gap-2 border border-seam py-3 text-sm font-semibold text-frost">
              <MessageCircle size={16} /> WhatsApp
            </a>
            <Link to="/rfq" onClick={() => setOpen(false)} data-testid="nav-mobile-rfq"
              className="flex-1 bg-gold py-3 text-center font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-spark">
              Request a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
